import React from "react";
import { Partner } from "../../models/Partner.ts";
import { getPartnerTracking } from "../../services/partner.service.ts";
import { notification } from "../../utils/Notifications.tsx";
import { format, parseISO } from "date-fns";
import { es } from "date-fns/locale";
import EmptyImage from "../../assets/img/ic_content.svg";
import { getProfileTabPaneClass } from "./profileTabUtils.ts";

interface TrackingProps {
  partner: Partner;
  reloadPage: () => Promise<void>;
  isTabActive?: boolean;
  embedded?: boolean;
}

interface TimelineEvent {
  id: string;
  type: string;
  date: string | Date;
  title: string;
  description: string;
  status?: string;
  icon: string;
  color: string;
  details?: Record<string, any>;
}

const Tracking: React.FC<TrackingProps> = ({
  partner,
  reloadPage,
  isTabActive = false,
  embedded = false,
}) => {
  const tabPaneClass = getProfileTabPaneClass(isTabActive, embedded);
  const [timelineEvents, setTimelineEvents] = React.useState<TimelineEvent[]>(
    []
  );
  const [loading, setLoading] = React.useState<boolean>(false);
  const [hasFetched, setHasFetched] = React.useState<boolean>(false);

  const fetchTracking = async () => {
    try {
      setLoading(true);
      const data = await getPartnerTracking(partner.id);
      // El backend ya devuelve los eventos en el formato correcto
      setTimelineEvents(Array.isArray(data) ? data : []);
    } catch (error) {
      notification("Error", "Error al cargar el tracking", "danger");
      setTimelineEvents([]);
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    const tabSelector = 'a[href="#tracking"]';
    const tabElement = document.querySelector(tabSelector);

    if (!tabElement) return;

    const checkAndFetch = () => {
      const isActive = tabElement.classList.contains("active");
      if (isActive && !hasFetched) {
        fetchTracking();
        setHasFetched(true);
      }
    };

    // Verificar si ya está activa al montar (por si llegan directo a la pestaña)
    checkAndFetch();

    // Bootstrap dispara 'shown.bs.tab' al cambiar de pestaña
    const handler = () => checkAndFetch();
    // @ts-ignore
    tabElement.addEventListener("shown.bs.tab", handler);
    tabElement.addEventListener("click", handler);

    return () => {
      // @ts-ignore
      tabElement.removeEventListener("shown.bs.tab", handler);
      tabElement.removeEventListener("click", handler);
    };
  }, [partner.id, hasFetched]);

  const formatDateDisplay = (date: Date | string | null) => {
    if (!date) return "No disponible";
    try {
      // Para el front usamos siempre la zona horaria local (navegador),
      // que en tu caso es horario de México.
      const dateObj = date instanceof Date ? date : parseISO(date);
      return format(dateObj, "dd 'de' MMMM yyyy, HH:mm 'hrs'", { locale: es });
    } catch {
      return String(date);
    }
  };

  const formatDateTimeDisplay = (dateString: string | Date | null) => {
    if (!dateString) return "No disponible";
    try {
      const date =
        dateString instanceof Date ? dateString : parseISO(dateString);
      return format(date, "dd 'de' MMMM yyyy, HH:mm 'hrs'", { locale: es });
    } catch {
      return formatDateDisplay(dateString);
    }
  };

  // Función dinámica para renderizar detalles
  const renderDetails = (details: Record<string, any> | undefined) => {
    if (!details || Object.keys(details).length === 0) return null;

    const detailLabels: Record<string, string> = {
      expectativaSalarial: "Expectativa salarial",
      fechaCompleta: "Fecha y hora",
      salario: "Salario",
      salarioAnterior: "Salario anterior",
      salarioNuevo: "Salario nuevo",
      periodicidad: "Periodicidad",
      turno: "Turno",
      talla: "Talla",
      sucursal: "Sucursal",
      razon: "Razón",
      notas: "Notas",
      comentarios: "Comentarios",
      comentario: "Comentario",
      solicitadoPor: "Solicitado por",
      actualizadoPor: "Actualizado por",
      realizadoPor: "Realizado por",
      autorizadoPor: "Autorizado por",
      fechaBaja: "Fecha de baja",
      numeroReloj: "Número de reloj",
      estado: "Estado",
      dias: "Días",
      fechaInicio: "Fecha inicio",
      fechaFin: "Fecha fin",
      revisadoPor: "Revisado por",
      tipoDocumento: "Tipo de documento",
      subidoPor: "Subido por",
      nombreArchivo: "Nombre de archivo",
      extension: "Extensión",
      requiereLiberacion: "Requiere liberación",
      aprobadoPorResponsable: "Aprobado por responsable",
      responsableVacaciones: "Responsable de vacaciones",
      fechaAprobacion: "Fecha de aprobación",
      fechaRechazo: "Fecha de rechazo",
    };

    return (
      <div
        style={{
          marginTop: "8px",
          paddingTop: "8px",
          borderTop: "1px solid #e9ecef",
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))",
          gap: "6px",
        }}
      >
        {Object.entries(details).map(([key, value]) => {
          if (value === null || value === undefined || value === "")
            return null;

          const label = detailLabels[key] || key;
          let displayValue: string | React.ReactNode = String(value);

          // Formatear valores especiales
          if (key.includes("salario") || key === "expectativaSalarial") {
            displayValue = (
              <span
                style={{
                  color: "#28a745",
                  fontWeight: "600",
                  fontSize: "11px",
                }}
              >
                ${Number(value).toLocaleString()}
              </span>
            );
          } else if (key.includes("fecha") && typeof value === "string") {
            displayValue = formatDateDisplay(value);
          } else if (key === "fechaCompleta") {
            displayValue = formatDateTimeDisplay(value);
          } else if (key === "numeroReloj") {
            displayValue = (
              <span
                style={{
                  color: "#dc3545",
                  fontWeight: "600",
                  fontSize: "11px",
                }}
              >
                {String(value)}
              </span>
            );
          } else if (
            key === "requiereLiberacion" ||
            key === "aprobadoPorResponsable"
          ) {
            displayValue = value ? "Sí" : "No";
          } else if (key === "dias") {
            displayValue = `${value} día${Number(value) !== 1 ? "s" : ""}`;
          }

          return (
            <div
              key={key}
              style={{
                padding: "4px 6px",
                background: "#f8f9fa",
                borderRadius: "4px",
                border: "1px solid #e9ecef",
              }}
            >
              <div
                style={{
                  fontSize: "8px",
                  color: "#868e96",
                  fontWeight: "600",
                  textTransform: "uppercase",
                  letterSpacing: "0.2px",
                  marginBottom: "3px",
                }}
              >
                {label}
              </div>
              <div
                style={{
                  color: "#212529",
                  fontSize: "10px",
                  fontWeight: "500",
                  lineHeight: "1.2",
                }}
              >
                {displayValue}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  if (loading) {
    return (
      <div
        className={tabPaneClass}
        id="tracking"
        style={{
          padding: embedded ? "20px 0" : "40px 20px",
          background: embedded ? "transparent" : "#f8f9fa",
          minHeight: embedded ? "auto" : "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <div style={{ textAlign: "center" }}>
          <div
            style={{
              width: "48px",
              height: "48px",
              border: "4px solid #e9ecef",
              borderTop: "4px solid #dc3545",
              borderRadius: "50%",
              animation: "spin 0.8s linear infinite",
              margin: "0 auto 16px",
            }}
          ></div>
          <p style={{ color: "#6c757d", fontSize: "14px", margin: 0 }}>
            Cargando...
          </p>
          <style>{`
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
          `}</style>
        </div>
      </div>
    );
  }

  if (timelineEvents.length === 0) {
    return (
      <div className={tabPaneClass} id="tracking">
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            padding: "60px 20px",
            minHeight: "400px",
          }}
        >
          <img
            src={EmptyImage}
            alt=""
            style={{ width: "250px", marginBottom: "20px" }}
          />
          <h5
            style={{
              color: "#6c757d",
              fontSize: "20px",
              fontWeight: "500",
            }}
          >
            No hay eventos en el tracking
          </h5>
        </div>
      </div>
    );
  }

  return (
    <div
      className={tabPaneClass}
      id="tracking"
      style={{
        padding: embedded ? "0" : "24px 16px",
        background: embedded ? "transparent" : "#f8f9fa",
        minHeight: embedded ? "auto" : "100vh",
      }}
    >
      <div style={{ textAlign: "center", marginBottom: "32px" }}>
        <h2
          style={{
            fontSize: "24px",
            fontWeight: "700",
            color: "#1a1a1a",
            margin: "0 0 6px 0",
            letterSpacing: "-0.3px",
          }}
        >
          Traking del colaborador
        </h2>
        <p
          style={{
            fontSize: "13px",
            color: "#6c757d",
            margin: 0,
            fontWeight: "400",
          }}
        >
          Historial del colaborador
        </p>
      </div>

      <div
        style={{
          maxWidth: "1200px",
          margin: "0 auto",
          position: "relative",
        }}
      >
        <div
          style={{
            position: "absolute",
            left: "50%",
            top: "0",
            bottom: "0",
            width: "3px",
            background:
              "linear-gradient(180deg, #dc3545 0%, #c82333 50%, #bd2130 100%)",
            transform: "translateX(-50%)",
            borderRadius: "2px",
            boxShadow: "0 0 12px rgba(220, 53, 69, 0.3)",
          }}
        />

        {timelineEvents.map((event, index) => {
          const isEven = index % 2 === 0;
          const eventDate =
            event.date instanceof Date
              ? event.date
              : parseISO(event.date as string);

          return (
            <div
              key={event.id}
              style={{
                position: "relative",
                marginBottom: "32px",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
                animation: `slideIn${isEven ? "Left" : "Right"} 0.5s ease-out`,
                animationDelay: `${index * 0.05}s`,
                animationFillMode: "both",
              }}
            >
              <div
                style={{
                  width: "calc(50% - 30px)",
                  position: "relative",
                  zIndex: 2,
                  ...(isEven ? { order: 1 } : { order: 3 }),
                }}
              >
                <div
                  style={{
                    background: "white",
                    borderRadius: "8px",
                    padding: "10px 12px",
                    boxShadow: `
                    0 2px 6px rgba(0, 0, 0, 0.05),
                    0 0 0 1px rgba(0, 0, 0, 0.02)
                  `,
                    transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                    border: `1px solid ${event.color}10`,
                    position: "relative",
                    overflow: "hidden",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform =
                      "translateY(-2px) scale(1.01)";
                    e.currentTarget.style.boxShadow = `
                    0 4px 16px rgba(0, 0, 0, 0.08),
                    0 0 0 1px rgba(0, 0, 0, 0.04),
                    0 0 0 2px ${event.color}12
                  `;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = "translateY(0) scale(1)";
                    e.currentTarget.style.boxShadow = `
                    0 2px 6px rgba(0, 0, 0, 0.05),
                    0 0 0 1px rgba(0, 0, 0, 0.02)
                  `;
                  }}
                >
                  <div
                    style={{
                      position: "absolute",
                      [isEven ? "right" : "left"]: "0",
                      top: "0",
                      bottom: "0",
                      width: "2px",
                      background: `linear-gradient(180deg, ${event.color} 0%, ${event.color}dd 100%)`,
                      borderRadius: isEven ? "0 8px 8px 0" : "8px 0 0 8px",
                    }}
                  />

                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      marginBottom: "8px",
                    }}
                  >
                    <div
                      style={{
                        width: "28px",
                        height: "28px",
                        borderRadius: "6px",
                        background: `linear-gradient(135deg, ${event.color} 0%, ${event.color}dd 100%)`,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: "white",
                        fontSize: "13px",
                        boxShadow: `0 1px 4px ${event.color}30`,
                        flexShrink: 0,
                      }}
                    >
                      <i className={`fa-solid ${event.icon}`}></i>
                    </div>
                    <div style={{ flex: 1 }}>
                      <div
                        style={{
                          fontSize: "10px",
                          color: "#868e96",
                          fontWeight: "500",
                          display: "flex",
                          alignItems: "center",
                          gap: "4px",
                        }}
                      >
                        <i
                          className="fa-solid fa-calendar"
                          style={{ fontSize: "8px" }}
                        ></i>
                        {formatDateDisplay(eventDate)}
                      </div>
                    </div>
                  </div>

                  <h3
                    style={{
                      fontSize: "13px",
                      fontWeight: "700",
                      color: "#1a1a1a",
                      margin: "0 0 4px 0",
                      lineHeight: "1.3",
                    }}
                  >
                    {event.title}
                  </h3>

                  <p
                    style={{
                      fontSize: "11px",
                      color: "#495057",
                      margin: "0 0 8px 0",
                      lineHeight: "1.4",
                    }}
                  >
                    {event.description}
                  </p>

                  {renderDetails(event.details)}
                </div>
              </div>

              <div
                style={{
                  position: "absolute",
                  left: "50%",
                  top: "10px",
                  transform: "translateX(-50%)",
                  width: "32px",
                  height: "32px",
                  borderRadius: "50%",
                  background: "white",
                  border: `2.5px solid ${event.color}`,
                  boxShadow: `
                  0 0 0 4px #f8f9fa,
                  0 2px 8px rgba(0, 0, 0, 0.1),
                  0 0 16px ${event.color}20
                `,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: event.color,
                  fontSize: "14px",
                  zIndex: 10,
                  transition: "all 0.2s ease",
                  cursor: "pointer",
                  order: 2,
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform =
                    "translateX(-50%) scale(1.1)";
                  e.currentTarget.style.boxShadow = `
                  0 0 0 4px #f8f9fa,
                  0 3px 16px rgba(0, 0, 0, 0.12),
                  0 0 24px ${event.color}35
                `;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = "translateX(-50%) scale(1)";
                  e.currentTarget.style.boxShadow = `
                  0 0 0 4px #f8f9fa,
                  0 2px 8px rgba(0, 0, 0, 0.1),
                  0 0 16px ${event.color}20
                `;
                }}
              >
                <i className={`fa-solid ${event.icon}`}></i>
              </div>

              <div
                style={{
                  width: "calc(50% - 30px)",
                  ...(isEven ? { order: 3 } : { order: 1 }),
                }}
              />
            </div>
          );
        })}
      </div>

      <style>{`
        @keyframes slideInLeft {
          from {
            opacity: 0;
            transform: translateX(-30px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }
        
        @keyframes slideInRight {
          from {
            opacity: 0;
            transform: translateX(30px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }
      `}</style>
    </div>
  );
};

export default Tracking;
